import { z } from 'zod';

/**
 * Why the readiness chain stopped without the analysis ever becoming readable.
 *
 * Two endings, because there are two ways to know an answer is not coming and they are not the
 * same fact. Remesh can say so — a study it never analysed, an analysis that failed, a status it
 * could not resolve — or it can say nothing conclusive for long enough that continuing to wait is
 * a claim rather than patience. Both stop the chain; neither moves the study, because the
 * lifecycle has no state for "was being analysed and now is not" and inventing one would put a
 * study somewhere nothing downstream knows how to read.
 */
export const READINESS_ESCALATIONS = ['analysis_unavailable', 'timed_out'] as const;
export const readinessEscalationSchema = z.enum(READINESS_ESCALATIONS);
export type ReadinessEscalation = z.infer<typeof readinessEscalationSchema>;

/**
 * What the readiness poller wrote down when it gave up, as it is stored on the study and rendered.
 *
 * Written only on an ending. There is no row for "still waiting", because the study's own state
 * already says that and a second field repeating it could only ever disagree with it. Stored in
 * snake_case like every other jsonb payload here, and parsed on read rather than trusted.
 */
export const storedReadinessEscalationSchema = z.object({
  escalation: readinessEscalationSchema,
  /**
   * Remesh's own word for where the analysis stood, verbatim, or null where it offered none.
   *
   * A plain string rather than the reason enum: that vocabulary belongs to the Remesh package,
   * which this one sits underneath, and a copy of it here would be a second list to keep in step.
   * Nothing branches on it — it is shown beside the sentence so a stalled demo can be diagnosed
   * without going to the logs.
   */
  reason: z.string().nullable(),
});
export type StoredReadinessEscalation = z.infer<typeof storedReadinessEscalationSchema>;
